import axios, { AxiosError, AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import { DEFAULT_TIMEOUT } from '../../clients';

export type ApiEndpoint = { [key: string]: string };

export interface ApiInterface {
  apiEndpoints: ApiEndpoint;
}

/**
 * Abstract API client class that provides common functionality for making HTTP requests using Axios.
 */
abstract class BaseAxiosClient {
  private readonly axiosInstance: AxiosInstance;

  /**
   * Creates an instance of the AbstractApiClient.
   * @param baseURL - The base URL for the API requests.
   */
  protected constructor(baseURL: string) {
    this.axiosInstance = axios.create({ baseURL, timeout: DEFAULT_TIMEOUT });
    this.setupInterceptors();
  }

  /**
   * Make a GET request to the API.
   * @param url - The endpoint URL.
   * @param params - Optional query parameters.
   * @param cancelToken - Optional cancellation token source.
   * @returns A Promise containing the response data.
   */
  get<T>(url: string, params?: object, cancelToken?: CancelToken): Promise<T> {
    return this.axiosInstance.get(url, { params, cancelToken });
  }

  /**
   * Make a POST request to the API.
   * @param url - The endpoint URL.
   * @param data - Optional request payload.
   * @param cancelToken - Optional cancellation token source.
   * @returns A Promise containing the response data.
   */
  post<T>(url: string, data?: object, cancelToken?: CancelToken): Promise<T> {
    return this.axiosInstance.post(url, data, { cancelToken });
  }

  /**
   * Make a PUT request to the API.
   * @param url - The endpoint URL.
   * @param data - Optional request payload.
   * @param cancelToken - Optional cancellation token source.
   * @returns A Promise containing the response data.
   */
  put<T>(url: string, data?: object, cancelToken?: CancelToken): Promise<T> {
    return this.axiosInstance.put(url, data, { cancelToken });
  }

  /**
   * Make a DELETE request to the API.
   * @param url - The endpoint URL.
   * @param cancelToken - Optional cancellation token source.
   * @returns A Promise containing the response data.
   */
  delete<T>(url: string, cancelToken?: CancelToken): Promise<T> {
    return this.axiosInstance.delete(url, { cancelToken });
  }

  /**
   * Set up response interceptors for the Axios instance.
   * This method can be overridden in derived classes for additional configuration.
   */
  private setupInterceptors() {
    this.axiosInstance.interceptors.response.use(this.handleSuccess, this.handleError);
  }

  /**
   * Handle successful responses by extracting and returning the response data.
   * @param response - The AxiosResponse object.
   * @returns The response data.
   */
  private handleSuccess(response: AxiosResponse) {
    return response.data;
  }

  /**
   * Handle errors during API requests, log details, and throw the error.
   * @param error - The AxiosError object.
   * @throws The original AxiosError after logging details.
   */
  private handleError(error: AxiosError<unknown>) {
    if (axios.isCancel(error)) {
      console.log('Request canceled:', error.message);
    } else {
      error = error as AxiosError<unknown>;
      console.error('API request failed:', error.message || 'Unknown error');

      if (error.response && error.response.data) console.error('Error data:', error.response.data);
    }
    throw error;
  }
}

export { BaseAxiosClient };
